import { MagnifyingGlassIcon } from '@phosphor-icons/react';
import { useField } from 'formik';
import { useState } from 'react';

import { Button } from '../../components/button/Button';
import { Combobox } from '../../components/combobox/Combobox';
import { InputWrapper } from '../../components/InputWrapper';
import { useLocations } from './models';
import { getFullUrl } from '../../utils/url';
import { openTab } from '../../utils/windows';
import { CreateLocationDialog } from './components/CreateLocationDialog';

export type Location = {
  id: number;
  name: string;
  country: string;
  city: string;
};

export interface IMinimalLocation {
  id: number;
  name: string;
  country: string;
  city: string;
}

const displayLocation = (location: IMinimalLocation) => {
  return `${location.name} - ${location.city} ${location.country}`;
};

export interface ILocationComboboxProps {
  value: IMinimalLocation | null;
  onChange: (val: IMinimalLocation | null) => void;
  onBlur?: () => void;
  invalidText?: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
  hideInvalid?: boolean;
  hideViewLocation?: boolean;
  isNullable?: boolean;
}

export const LocationCombobox: React.FC<ILocationComboboxProps> = (props) => {
  const {
    value,
    onChange,
    onBlur,
    invalidText,
    labelText,
    helperText,
    isDisabled,
    hideViewLocation,
    isNullable = true,
    hideInvalid,
  } = props;
  const [searchValue, setSearchValue] = useState('');
  const [createState, setCreateState] = useState({ isOpen: false, name: '' });
  const { data } = useLocations({
    search: searchValue,
    onlyActive: true,
  });

  const locations = data ?? [];

  const openLocation = (id: number) => openTab(getFullUrl(`/internal/locations/${id}/general`));
  return (
    <div className="flex gap-2">
      <InputWrapper
        labelText={labelText ?? 'Locatie'}
        invalidText={invalidText}
        helperText={helperText}
        hideInvalid={hideInvalid}
      >
        <Combobox
          keyName="id"
          placeholder="Zoek een locatie..."
          onQueryChange={setSearchValue}
          display={displayLocation}
          items={locations}
          selectedItem={value}
          onSelect={(val) => {
            onChange(val);
          }}
          createLabel="Maak locatie aan"
          onCreate={(query) => setCreateState({ isOpen: true, name: query })}
          onBlur={onBlur}
          isDisabled={isDisabled}
          isInvalid={!!invalidText}
          isNullable={isNullable}
        />
      </InputWrapper>
      {!!hideViewLocation && (
        <div className="flex items-center">
          <Button
            color="secondary"
            onTrigger={() => {
              if (value) {
                openLocation(value.id);
              }
            }}
            isDisabled={!value}
          >
            <MagnifyingGlassIcon className="button-icon" />
          </Button>
        </div>
      )}

      <CreateLocationDialog
        isOpen={createState.isOpen}
        initialName={createState.name}
        onClose={() => setCreateState({ isOpen: false, name: '' })}
        onCreated={(created) => {
          onChange(created);
          setCreateState({ isOpen: false, name: '' });
        }}
      />
    </div>
  );
};

export interface ILocationComboboxFieldProps {
  name: string;
  labelText?: string;
  helperText?: string;
  isDisabled?: boolean;
}

export const LocationComboboxField: React.FC<ILocationComboboxFieldProps> = (props) => {
  const { name, labelText, helperText, isDisabled } = props;
  const [field, meta, helpers] = useField({ name });

  return (
    <LocationCombobox
      value={field.value}
      onChange={(val) => helpers.setValue(val)}
      onBlur={() => helpers.setTouched(true)}
      invalidText={(meta.touched && meta.error) || undefined}
      labelText={labelText}
      helperText={helperText}
      isDisabled={isDisabled}
    />
  );
};
